// AVRASTRO AI Content Studio — credits (requirement 10; owner decision D13, 22 Oct 2026; docs/content-studio/FEASIBILITY.md §5).
// 1 credit ≈ €0.01 of PROVIDER cost. The CRM add-on gives 500 credits a month, the standalone plans from 1,000. Nothing is
// unlimited: the platform reserves the credits of an action BEFORE it calls a provider (hard cap: no balance, no call),
// refunds them automatically when the provider fails, and the app shows the estimate before every costly action (the
// server refuses a costly action whose estimate the user did not confirm). Shared by the app (estimates) and the server.

export const CREDIT_COSTS = {
  /** copy draft (post, captions, script, CTAs, campaign ideas) incl. the AI reviewer's second pass */
  copy: 1,
  /** one image variant, standard model */
  image_standard: 4,
  /** one image variant, premium model / high quality */
  image_premium: 15,
  /** voice-over per started 30 s */
  voice_30s: 5,
  /** transcription per started 10 minutes of uploads */
  transcribe_10min: 1,
  /** one reel render (≤ 60 s) */
  reel_render: 5,
  /** AI music bed (≤ 60 s) — not offered until its licence scope is verified */
  music_ai: 15,
  /** AI b-roll shot, 8 s (Veo-class): fast / premium */
  broll_fast: 100,
  broll_premium: 350,
} as const;
export type CreditAction = keyof typeof CREDIT_COSTS;
export const CREDIT_ACTIONS = Object.keys(CREDIT_COSTS) as CreditAction[];

/** actions at or above this many credits need the user's explicit confirmation of the estimate */
export const CONFIRM_ABOVE = 10;

export interface CreditEstimate { action: CreditAction; units: number; credits: number; confirm: boolean }
/** the credits an action will use: images per variant, voice per 30 s, transcription per 10 min, the rest per item */
export function estimateCredits(action: CreditAction, o: { n?: number; seconds?: number } = {}): CreditEstimate {
  const per = CREDIT_COSTS[action];
  let units = Math.max(1, Math.floor(o.n ?? 1));
  if (action === 'voice_30s') units = Math.max(1, Math.ceil((o.seconds ?? 30) / 30));
  if (action === 'transcribe_10min') units = Math.max(1, Math.ceil((o.seconds ?? 600) / 600));
  if (action === 'image_standard' || action === 'image_premium') units = Math.min(8, units);
  const credits = per * units;
  return { action, units, credits, confirm: credits >= CONFIRM_ABOVE };
}

/** the monthly allowance resets on the first day of the month (UTC), unused allowance expires; top-ups stay until used */
export const creditMonth = (iso: string) => iso.slice(0, 7);
export interface CreditBalance { month: string; allowance: number; allowanceLeft: number; topupLeft: number; total: number }
/** how a charge is split: the month's allowance first, then the top-ups (a refund returns each part where it came from) */
export function splitCharge(b: Pick<CreditBalance, 'allowanceLeft' | 'topupLeft'>, credits: number): { fromAllowance: number; fromTopup: number } | null {
  if (credits < 0 || !Number.isInteger(credits)) return null;
  if (b.allowanceLeft + b.topupLeft < credits) return null;
  const fromAllowance = Math.min(b.allowanceLeft, credits);
  return { fromAllowance, fromTopup: credits - fromAllowance };
}
